import { useMemo, useState } from "react";
import { Box, Typography, Chip, IconButton, Divider, Tooltip } from "@mui/material";
import { Close, ArrowBack, ArrowForward } from "@mui/icons-material";
import localize from "../../../common/localize";

const NODE_H = 56;
const NODE_PAD = 32;
const CHAR_W = 8;
const MIN_NODE_W = 120;
const H_GAP = 120;
const V_GAP = 100;

function getNodeWidth(name) {
  return Math.max(MIN_NODE_W, (name?.length || 0) * CHAR_W + NODE_PAD);
}

function buildGraph(statusChanges) {
  const nodesMap = new Map();
  const edges = [];

  statusChanges.forEach((sc) => {
    const initial = sc.InitialStatus;
    const final = sc.FinalStatus ?? sc.InitialStatus;

    if (initial && !nodesMap.has(initial.Id)) {
      nodesMap.set(initial.Id, { ...initial, w: getNodeWidth(initial.Name) });
    }
    if (final && !nodesMap.has(final.Id)) {
      nodesMap.set(final.Id, { ...final, w: getNodeWidth(final.Name) });
    }

    edges.push({
      from: initial?.Id,
      to: final?.Id,
      hasForm: !!sc.DynamicForm,
      formName: sc.DynamicForm?.Name,
      isAdmin: sc.IsAdministrative,
    });
  });

  const nodes = [...nodesMap.values()];

  const adjacency = new Map();
  edges.forEach((e) => {
    if (!adjacency.has(e.from)) adjacency.set(e.from, []);
    adjacency.get(e.from).push(e.to);
  });

  const levels = new Map();
  const visited = new Set();
  const queue = [];

  const roots = nodes.filter(
    (n) => !edges.some((e) => e.to === n.Id && e.from !== n.Id)
  );
  if (roots.length === 0 && nodes.length > 0) {
    roots.push(nodes[0]);
  }

  roots.forEach((r) => {
    levels.set(r.Id, 0);
    visited.add(r.Id);
    queue.push(r.Id);
  });

  while (queue.length > 0) {
    const current = queue.shift();
    const currentLevel = levels.get(current);
    const children = adjacency.get(current) || [];
    children.forEach((childId) => {
      if (!visited.has(childId)) {
        visited.add(childId);
        levels.set(childId, currentLevel + 1);
        queue.push(childId);
      }
    });
  }

  nodes.forEach((n) => {
    if (!levels.has(n.Id)) {
      levels.set(n.Id, 0);
    }
  });

  const columns = new Map();
  nodes.forEach((n) => {
    const col = levels.get(n.Id);
    if (!columns.has(col)) columns.set(col, []);
    columns.get(col).push(n);
  });

  const positioned = [];
  const maxRows = Math.max(...[...columns.values()].map((c) => c.length), 1);

  const colOffsets = new Map();
  let xCursor = 40;
  const sortedCols = [...columns.keys()].sort((a, b) => a - b);
  sortedCols.forEach((col) => {
    colOffsets.set(col, xCursor);
    const maxW = Math.max(...columns.get(col).map((n) => n.w));
    xCursor += maxW + H_GAP;
  });

  columns.forEach((colNodes, col) => {
    const totalH = colNodes.length * NODE_H + (colNodes.length - 1) * V_GAP;
    const maxH = maxRows * NODE_H + (maxRows - 1) * V_GAP;
    const startY = (maxH - totalH) / 2;

    colNodes.forEach((n, row) => {
      positioned.push({
        ...n,
        x: colOffsets.get(col),
        y: startY + row * (NODE_H + V_GAP),
      });
    });
  });

  const PADDING = 60;
  const minY = Math.min(...positioned.map((n) => n.y));
  const offsetY = PADDING - minY;
  positioned.forEach((n) => {
    n.y += offsetY;
  });

  return { nodes: positioned, edges };
}

function getEdgePath(fromNode, toNode) {
  const x1 = fromNode.x + fromNode.w;
  const y1 = fromNode.y + NODE_H / 2;
  const x2 = toNode.x;
  const y2 = toNode.y + NODE_H / 2;

  if (fromNode.Id === toNode.Id) {
    const cx = fromNode.x + fromNode.w / 2;
    const cy = fromNode.y;
    const r = 24;
    return {
      path: `M ${cx - r} ${cy} A ${r} ${r} 0 1 1 ${cx + r} ${cy}`,
      labelX: cx,
      labelY: cy - r - 10,
      isSelf: true,
    };
  }

  const dx = x2 - x1;
  const cp1x = x1 + dx * 0.4;
  const cp2x = x1 + dx * 0.6;

  return {
    path: `M ${x1} ${y1} C ${cp1x} ${y1}, ${cp2x} ${y2}, ${x2} ${y2}`,
    labelX: (x1 + x2) / 2,
    labelY: (y1 + y2) / 2 - 12,
    isSelf: false,
  };
}

function EdgeBadge({ label, color, tooltip }) {
  return (
    <Tooltip title={tooltip} arrow>
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: "4px",
          bgcolor: color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#fff", lineHeight: 1 }}>
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function StatusChip({ node }) {
  return (
    <Chip
      label={node.Name}
      size="small"
      sx={{
        bgcolor: node.BackgroundColor || "#002121",
        color: node.ForegroundColor || "#FFFFFF",
        fontWeight: 600,
        fontSize: 12,
        maxWidth: "100%",
      }}
    />
  );
}

export default function StatusChangeGraph({ statusChanges, onClose }) {
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  const { nodes, edges } = useMemo(
    () => buildGraph(statusChanges || []),
    [statusChanges]
  );

  const nodeMap = new Map(nodes.map((n) => [n.Id, n]));

  const panelData = useMemo(() => {
    if (!selectedNodeId) return null;
    const node = nodeMap.get(selectedNodeId);
    if (!node) return null;

    const prevMap = new Map();
    const nextMap = new Map();
    edges.forEach((e) => {
      if (e.to === selectedNodeId && e.from !== selectedNodeId) {
        if (!prevMap.has(e.from)) prevMap.set(e.from, { hasForm: e.hasForm, formName: e.formName, isAdmin: e.isAdmin });
      }
      if (e.from === selectedNodeId && e.to !== selectedNodeId) {
        if (!nextMap.has(e.to)) nextMap.set(e.to, { hasForm: e.hasForm, formName: e.formName, isAdmin: e.isAdmin });
      }
    });

    return {
      node,
      prev: [...prevMap.entries()].map(([id, meta]) => ({ node: nodeMap.get(id), ...meta })).filter((p) => p.node),
      next: [...nextMap.entries()].map(([id, meta]) => ({ node: nodeMap.get(id), ...meta })).filter((p) => p.node),
    };
  }, [selectedNodeId, nodes, edges]);

  const svgW = nodes.length
    ? Math.max(...nodes.map((n) => n.x + n.w)) + 80
    : 400;
  const svgH = nodes.length
    ? Math.max(...nodes.map((n) => n.y + NODE_H)) + 80
    : 300;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        bgcolor: "rgba(0,0,0,0.6)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 1.5,
          bgcolor: "#10366e",
          color: "white",
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          {localize.status_changes} — {localize.graph}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", overflow: "hidden" }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: "auto",
            bgcolor: "#f8f9fa",
            p: 3,
            textAlign: "center",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget || e.target.tagName === "svg") {
              setSelectedNodeId(null);
            }
          }}
        >
          {nodes.length === 0 ? (
            <Typography color="text.secondary" variant="h6">
              {localize.no_status_changes}
            </Typography>
          ) : (
            <svg
              width={svgW}
              height={svgH}
              viewBox={`0 0 ${svgW} ${svgH}`}
              style={{ minWidth: svgW, minHeight: svgH }}
            >
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="10"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3.5, 0 7" fill="#90a4ae" />
                </marker>
                <marker
                  id="arrowhead-next"
                  markerWidth="10"
                  markerHeight="7"
                  refX="10"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3.5, 0 7" fill="#2e7d32" />
                </marker>
                <marker
                  id="arrowhead-prev"
                  markerWidth="10"
                  markerHeight="7"
                  refX="10"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3.5, 0 7" fill="#1976d2" />
                </marker>
                <filter id="nodeShadow" x="-10%" y="-10%" width="130%" height="140%">
                  <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15" />
                </filter>
              </defs>

              {edges.map((edge, i) => {
                const fromNode = nodeMap.get(edge.from);
                const toNode = nodeMap.get(edge.to);
                if (!fromNode || !toNode) return null;

                const { path, labelX, labelY, isSelf } = getEdgePath(
                  fromNode,
                  toNode
                );

                const isNext = selectedNodeId && edge.from === selectedNodeId;
                const isPrev = selectedNodeId && edge.to === selectedNodeId;
                const isHighlighted = isNext || isPrev;
                const edgeColor = isNext ? "#2e7d32" : isPrev ? "#1976d2" : "#90a4ae";
                const markerId = isNext ? "arrowhead-next" : isPrev ? "arrowhead-prev" : "arrowhead";

                return (
                  <g key={`edge-${i}`}>
                    <path
                      d={path}
                      fill="none"
                      stroke={edgeColor}
                      strokeWidth={isHighlighted ? 3 : 2}
                      markerEnd={`url(#${markerId})`}
                      strokeDasharray={isSelf ? "6 3" : "none"}
                      style={{ transition: "stroke 0.2s, stroke-width 0.2s" }}
                    />
                    {(edge.hasForm || edge.isAdmin) && (
                      <g>
                        {edge.hasForm && (
                          <>
                            <rect
                              x={labelX - 8}
                              y={labelY - 8}
                              width={16}
                              height={16}
                              rx={3}
                              fill="#ed6c02"
                              opacity={0.9}
                            />
                            <text
                              x={labelX}
                              y={labelY + 4}
                              textAnchor="middle"
                              fontSize={10}
                              fontWeight={700}
                              fill="white"
                            >
                              F
                            </text>
                            <title>{`${localize.form}: ${edge.formName}`}</title>
                          </>
                        )}
                        {edge.isAdmin && (
                          <>
                            <rect
                              x={labelX + (edge.hasForm ? 12 : -8)}
                              y={labelY - 8}
                              width={16}
                              height={16}
                              rx={3}
                              fill="#10366E"
                              opacity={0.9}
                            />
                            <text
                              x={labelX + (edge.hasForm ? 20 : 0)}
                              y={labelY + 4}
                              textAnchor="middle"
                              fontSize={10}
                              fontWeight={700}
                              fill="white"
                            >
                              W
                            </text>
                            <title>{localize.only_web}</title>
                          </>
                        )}
                      </g>
                    )}
                  </g>
                );
              })}

              {nodes.map((node) => {
                const isSelected = node.Id === selectedNodeId;
                return (
                  <g
                    key={`node-${node.Id}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelectedNodeId(node.Id)}
                  >
                    {isSelected && (
                      <rect
                        x={node.x - 4}
                        y={node.y - 4}
                        width={node.w + 8}
                        height={NODE_H + 8}
                        rx={12}
                        ry={12}
                        fill="none"
                        stroke="#1976d2"
                        strokeWidth={3}
                      />
                    )}
                    <rect
                      x={node.x}
                      y={node.y}
                      width={node.w}
                      height={NODE_H}
                      rx={10}
                      ry={10}
                      fill={node.BackgroundColor || "#002121"}
                      filter="url(#nodeShadow)"
                    />
                    <rect
                      x={node.x}
                      y={node.y}
                      width={node.w}
                      height={NODE_H}
                      rx={10}
                      ry={10}
                      fill="none"
                      stroke={node.ForegroundColor || "#FFFFFF"}
                      strokeWidth={1}
                      strokeOpacity={0.3}
                    />
                    <text
                      x={node.x + node.w / 2}
                      y={node.y + NODE_H / 2 + 1}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill={node.ForegroundColor || "#FFFFFF"}
                      fontSize={13}
                      fontWeight={600}
                      fontFamily="Roboto, sans-serif"
                    >
                      {node.Name}
                    </text>
                  </g>
                );
              })}
            </svg>
          )}
        </Box>

        <Box
          sx={{
            width: selectedNodeId ? 300 : 0,
            flexShrink: 0,
            overflow: "hidden",
            transition: "width 0.3s ease",
            borderLeft: selectedNodeId ? "1px solid" : "none",
            borderColor: "divider",
            bgcolor: "#fff",
          }}
        >
          {panelData && (
            <Box sx={{ width: 300, height: "100%", display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  flexShrink: 0,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  {localize.status_changes}
                </Typography>
                <IconButton size="small" onClick={() => setSelectedNodeId(null)}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <StatusChip node={panelData.node} />
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                    <ArrowBack sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">
                      {localize.previous || "Previous"}
                    </Typography>
                  </Box>
                  {panelData.prev.length === 0 ? (
                    <Typography variant="body2" color="text.disabled" sx={{ pl: 3 }}>
                      —
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, pl: 3 }}>
                      {panelData.prev.map((p) => (
                        <Box
                          key={p.node.Id}
                          sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                          onClick={() => setSelectedNodeId(p.node.Id)}
                        >
                          <StatusChip node={p.node} />
                          {p.hasForm && <EdgeBadge label="F" color="#ed6c02" tooltip={`${localize.form}: ${p.formName}`} />}
                          {p.isAdmin && <EdgeBadge label="W" color="#10366E" tooltip={localize.only_web} />}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                    <ArrowForward sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase">
                      {localize.next || "Next"}
                    </Typography>
                  </Box>
                  {panelData.next.length === 0 ? (
                    <Typography variant="body2" color="text.disabled" sx={{ pl: 3 }}>
                      —
                    </Typography>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, pl: 3 }}>
                      {panelData.next.map((p) => (
                        <Box
                          key={p.node.Id}
                          sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                          onClick={() => setSelectedNodeId(p.node.Id)}
                        >
                          <StatusChip node={p.node} />
                          {p.hasForm && <EdgeBadge label="F" color="#ed6c02" tooltip={`${localize.form}: ${p.formName}`} />}
                          {p.isAdmin && <EdgeBadge label="W" color="#10366E" tooltip={localize.only_web} />}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 3,
          py: 1,
          bgcolor: "#fff",
          borderTop: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "3px",
              bgcolor: "#ed6c02",
            }}
          />
          <Typography variant="caption" color="text.secondary">
            F = {localize.form}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: "3px",
              bgcolor: "#10366E",
            }}
          />
          <Typography variant="caption" color="text.secondary">
            W = {localize.only_web}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
